import React from "react";
import { Table, Tag, Button, Tooltip, Space, Progress } from "antd";
import {
  EyeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const ReconciliationTable = ({
  reconciliations,
  loading,
  onViewDetails,
  onComplete,
  onMarkForReview,
  onDelete,
}) => {
  const getStatusColor = (status) => {
    const statusColors = {
      Completed: "green",
      "In Progress": "blue",
      "Requires Review": "orange",
    };
    return statusColors[status] || "default";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CheckCircleOutlined />;
      case "Requires Review":
        return <ExclamationCircleOutlined />;
      default:
        return null;
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "reconciliationDate",
      key: "reconciliationDate",
      width: 120,
      render: (date) => (
        <a onClick={() => onViewDetails({ reconciliationDate: date })}>
          {date ? dayjs(date).format("DD MMM YYYY") : "-"}
        </a>
      ),
      sorter: (a, b) =>
        new Date(a.reconciliationDate) - new Date(b.reconciliationDate),
      defaultSortOrder: "descend",
    },
    {
      title: "Total Payments",
      dataIndex: "totalPayments",
      key: "totalPayments",
      width: 150,
      align: "right",
      render: (amount) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(amount)}</span>
      ),
    },
    {
      title: "Valid Payments",
      dataIndex: "totalValidPayments",
      key: "totalValidPayments",
      width: 150,
      align: "right",
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: "Suspense",
      dataIndex: "totalSuspensePayments",
      key: "totalSuspensePayments",
      width: 130,
      align: "right",
      render: (amount) => (
        <span
          style={{ fontWeight: 600, color: amount > 0 ? "#ff4d4f" : "inherit" }}
        >
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: "Payment Count",
      dataIndex: "paymentCount",
      key: "paymentCount",
      width: 120,
      align: "center",
      render: (count) => <Tag color="blue">{count || 0}</Tag>,
    },
    {
      title: "Suspense Count",
      dataIndex: "suspenseCount",
      key: "suspenseCount",
      width: 130,
      align: "center",
      render: (count) => (
        <Tag color={count > 0 ? "red" : "default"}>{count || 0}</Tag>
      ),
    },
    {
      title: "Reconciliation Rate",
      key: "reconciliationRate",
      width: 150,
      render: (_, record) => {
        const total = parseFloat(record.totalPayments) || 0;
        const valid = parseFloat(record.totalValidPayments) || 0;
        const rate = total > 0 ? Math.floor((valid / total) * 100) : 0;
        return (
          <Progress
            percent={rate}
            size="small"
            status={
              rate === 100 ? "success" : rate >= 80 ? "active" : "exception"
            }
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
      filters: [
        { text: "Completed", value: "Completed" },
        { text: "In Progress", value: "In Progress" },
        { text: "Requires Review", value: "Requires Review" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Bank Statement",
      dataIndex: "bankStatementFile",
      key: "bankStatementFile",
      width: 120,
      align: "center",
      render: (file) =>
        file ? (
          <Tooltip title="View Bank Statement">
            <Button
              type="link"
              icon={<FileTextOutlined />}
              href={file}
              target="_blank"
            />
          </Tooltip>
        ) : (
          <Tag color="default">None</Tag>
        ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY HH:mm") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails(record)}
            />
          </Tooltip>
          {record.status !== "Completed" && (
            <Tooltip title="Mark Complete">
              <Button
                type="link"
                icon={<CheckCircleOutlined />}
                style={{ color: "#52c41a" }}
                onClick={() => onComplete(record)}
              />
            </Tooltip>
          )}
          {record.status === "In Progress" && (
            <Tooltip title="Mark for Review">
              <Button
                type="link"
                icon={<ExclamationCircleOutlined />}
                style={{ color: "#faad14" }}
                onClick={() => onMarkForReview(record)}
              />
            </Tooltip>
          )}
          {record.status !== "Completed" && (
            <Tooltip title="Delete">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={reconciliations}
      rowKey="reconciliationId"
      loading={loading}
      scroll={{ x: 1600 }}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} reconciliations`,
        pageSizeOptions: ["10", "20", "50", "100"],
      }}
    />
  );
};

export default ReconciliationTable;
